import { z } from 'zod';

const sharedFrameSchema = z.object({
  year: z.number().int(),
  b64: z.string().min(1),
});

const shareJourneyResponseSchema = z.object({
  id: z.string().min(1),
  url: z.string().min(1),
});

const remoteJourneySchema = z.object({
  id: z.string().min(1),
  location: z.string().min(1),
  prompt: z.string(),
  author: z.string().min(1).optional(),
  createdAt: z.number().int(),
  frames: z.array(
    z.object({
      year: z.number().int(),
      url: z.string().min(1),
    }),
  ),
});

export class ConfigError extends Error {
  constructor() {
    super('Missing VITE_WORKER_URL configuration.');
    this.name = 'ConfigError';
  }
}

export class ApiError extends Error {
  status: number;
  bodyText: string;

  constructor(status: number, bodyText: string) {
    super(`API request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

export type ShareJourneyArgs = {
  location: string;
  prompt: string;
  author?: string;
  frames: { year: number; b64: string }[];
};

export type ShareJourneyResult = { id: string; url: string };

export type RemoteJourney = {
  id: string;
  location: string;
  prompt: string;
  author?: string;
  createdAt: number;
  frames: Array<{ year: number; url: string }>;
};

export async function shareJourney(
  a: ShareJourneyArgs,
  signal?: AbortSignal,
): Promise<ShareJourneyResult> {
  const payload = {
    location: a.location.trim(),
    prompt: a.prompt.trim(),
    author: a.author?.trim() || undefined,
    frames: sharedFrameSchema.array().min(2).parse(a.frames),
  };

  const parsed = await requestJson('/journey', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });

  return shareJourneyResponseSchema.parse(parsed);
}

export async function fetchJourney(id: string, signal?: AbortSignal): Promise<RemoteJourney> {
  const parsed = await requestJson(`/journey/${encodeURIComponent(id)}`, {
    signal,
  });

  const journey = remoteJourneySchema.parse(parsed);
  return {
    ...journey,
    frames: [...journey.frames].sort((left, right) => left.year - right.year),
  };
}

export function frameAbsoluteUrl(rel: string): string {
  return new URL(rel, getWorkerBaseUrl()).toString();
}

async function requestJson(path: string, init: RequestInit): Promise<unknown> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(frameAbsoluteUrl(path), {
    ...init,
    headers,
  });

  const bodyText = await response.text();
  if (!response.ok) {
    throw new ApiError(response.status, bodyText);
  }

  try {
    return JSON.parse(bodyText);
  } catch (error) {
    throw new Error(`Malformed API response: ${getErrorMessage(error)}`);
  }
}

function getWorkerBaseUrl(): string {
  const configured = import.meta.env.VITE_WORKER_URL?.trim();
  if (!configured) {
    throw new ConfigError();
  }
  return configured.endsWith('/') ? configured : `${configured}/`;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
